/**
 * A markets-ticker tile for one tracked term: kind chip, label, headline value,
 * velocity (green/red arrow), the agenda·geo it's read off, and a sparkline.
 * Clickable → the term detail page.
 *
 * `headline` is null for a tracked term that has no scored metric series yet
 * (e.g. a quiet adjacent creator who hasn't co-occurred with wealth-tax
 * coverage) — the tile renders faded with a "no signal yet" state so the full
 * monitored set stays visible.
 */
import { Link } from "react-router-dom";
import { SALIENCE_AGENDA_LABELS } from "../../lib/api";
import { Sparkline } from "./Sparkline";
import { Freshness } from "./Freshness";
import {
  DIRECTION_ARROW,
  DIRECTION_CLASS,
  direction,
  formatHeadlineValue,
  formatHeadlineVelocity,
  GEO_LABELS,
  type Headline,
} from "./format";

const KIND_LABELS: Record<string, string> = {
  topic: "Topic",
  tagline: "Tagline",
  entity: "Entity",
};

interface MetricTileProps {
  termId: string;
  label: string;
  kind: string;
  headline: Headline | null;
  spark: number[];
}

export function MetricTile({ termId, label, kind, headline: h, spark }: MetricTileProps) {
  const dir = direction(h?.velocity);
  const quiet = h === null;

  return (
    <Link
      to={`/salience/terms/${termId}`}
      className={`group flex flex-col rounded-xl border p-4 transition-colors ${
        quiet
          ? "border-white/8 bg-terminal-panel/50 hover:border-white/20 hover:bg-terminal-panel"
          : "border-white/10 bg-terminal-panel hover:border-white/25 hover:bg-terminal-elevated"
      }`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
          {KIND_LABELS[kind] ?? kind}
        </span>
        {h ? (
          <span className="text-[10px] uppercase tracking-wider text-terminal-ink-muted">
            {SALIENCE_AGENDA_LABELS[h.agenda]} · {GEO_LABELS[h.geo] ?? h.geo}
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-terminal-ink-muted/60">
            tracked
          </span>
        )}
      </div>

      <div
        className={`truncate text-sm font-semibold ${
          quiet ? "text-terminal-ink-muted" : "text-terminal-ink"
        }`}
        title={label}
      >
        {label}
      </div>

      <div className="mt-2 flex items-end justify-between gap-2">
        <div>
          <div
            className={`font-mono text-2xl font-bold leading-none tabular-nums ${
              quiet ? "text-terminal-ink-muted/50" : "text-terminal-ink"
            }`}
          >
            {h ? formatHeadlineValue(h) : "—"}
          </div>
          {h ? (
            <div
              className={`mt-1 font-mono text-xs font-semibold tabular-nums ${DIRECTION_CLASS[dir]}`}
            >
              {DIRECTION_ARROW[dir]} {formatHeadlineVelocity(h)}
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-terminal-ink-muted/60">
              no signal yet
            </div>
          )}
        </div>
        {h && <Sparkline values={spark} />}
      </div>

      {h && <Freshness agenda={h.agenda} date={h.date} className="mt-2" />}
    </Link>
  );
}
